// The seed Desks (owner, 2026-10-12). First that day this file was the
// FIXED top block forced onto every Desk ("I need every manager desk to have
// these 5 items set up exactly the same way and have them not move at all");
// later the same day Tomer replaced that with desk templates he sets
// himself: "Allow me to move boxes around to set the desks how I want them
// to look — give me a LOCK DESK I can use which locks everyone's desk, and
// an EDIT LAYOUT to move around if I want." — and later still: "I want to
// edit the layout for myself and that carries to everyone" (a manager's or
// director's Desk is Tomer's own, panelCatalog.tsx ownerDeskFor).
//
// So nothing here is forced any more. The two registries are only the
// SEEDS — the top of a role's shipped default Desk (defaultLayouts.ts
// defaultLayoutFor packs the role's default panels under it) and the size an
// always-on panel is added back at: PERMANENT_PANEL_LAYOUT (Tomer's own arrangement; owner and director)
// and MANAGER_PANEL_LAYOUT (the compact one-clinic block). The five
// always-on panels must be PRESENT on every Desk (withPermanentPanels), but
// may sit anywhere.
//
// The server half is src/desk.js — kept in step by hand (standing rule 11:
// explicit registries), and client/tests/desk-templates.test.tsx fails if
// the two ever differ.
//
// Pure functions only (no preact, no catalog import) so panelCatalog.tsx can
// use this without an import cycle; the caller passes `mayShow` (which types
// the viewer's role may see).

export interface FixedSlot {
  type: string;
  col: number;
  row: number;
  w: number;
  h: number;
  config?: { clinic: string };
}

export const PERMANENT_PANEL_LAYOUT: readonly FixedSlot[] = [
  { type: 'clinicStrip', col: 0, row: 0, w: 12, h: 2 },
  { type: 'communications', col: 0, row: 2, w: 4, h: 4 },
  { type: 'needsAttention', col: 4, row: 2, w: 4, h: 2 },
  { type: 'spcAlerts', col: 8, row: 2, w: 4, h: 2 },
  { type: 'myMail', col: 4, row: 4, w: 8, h: 4 },
  { type: 'recruiting', col: 0, row: 6, w: 4, h: 2, config: { clinic: 'all' } },
];

/** The manager seed (owner, 2026-10-12): a MANAGER sees one clinic, so the
 * four-clinic strip is a small "Tasks" box — Tasks, Needs attention and SPC
 * side by side across the top, Communications and My Mail under them,
 * nvRecruiter under Communications. */
export const MANAGER_PANEL_LAYOUT: readonly FixedSlot[] = [
  { type: 'clinicStrip', col: 0, row: 0, w: 4, h: 2 },
  { type: 'needsAttention', col: 4, row: 0, w: 4, h: 2 },
  { type: 'spcAlerts', col: 8, row: 0, w: 4, h: 2 },
  { type: 'communications', col: 0, row: 2, w: 4, h: 4 },
  { type: 'myMail', col: 4, row: 2, w: 8, h: 4 },
  { type: 'recruiting', col: 0, row: 6, w: 4, h: 2, config: { clinic: 'all' } },
];

/** The seed block for one role, chosen by role only (standing rule 11).
 * src/desk.js fixedLayoutFor is the server twin. */
export function fixedLayoutFor(role: string | null | undefined): readonly FixedSlot[] {
  return role === 'MANAGER' ? MANAGER_PANEL_LAYOUT : PERMANENT_PANEL_LAYOUT;
}

const blockRows = (slots: readonly FixedSlot[]) => slots.reduce((m, s) => Math.max(m, s.row + s.h), 0);

/** How tall a seed block is — defaultLayoutFor packs the role's other
 * default panels below it. */
export const SEED_BLOCK_ROWS = Math.max(blockRows(PERMANENT_PANEL_LAYOUT), blockRows(MANAGER_PANEL_LAYOUT));

/** The five always-on panels. Mirrors src/desk.js PERMANENT_PANEL_TYPES
 * (and panelCatalog.tsx's `permanent` flags). */
export const ALWAYS_ON_TYPES: readonly string[] = ['needsAttention', 'communications', 'spcAlerts', 'recruiting', 'myMail'];

interface PanelLike {
  id: string;
  type: string;
  col: number;
  row: number;
  w: number;
  h: number;
  config?: { clinic?: string };
}

/**
 * Every always-on panel the role may see, present: a missing one is added
 * (id `permanent-<type>`, its seed size, clinic "all" for nvRecruiter) at
 * column 0 under everything else. Nothing already there moves. Input order
 * kept, additions appended. Idempotent. Same rule as src/desk.js
 * withPermanentPanels, so the screen matches what the server stores.
 */
export function withPermanentPanels<T extends PanelLike>(
  panels: readonly T[],
  mayShow: (type: string) => boolean,
  role?: string | null,
): T[] {
  const out: T[] = panels.map((p) => ({ ...p }));
  const ids = new Set(out.map((p) => p.id));
  for (const type of ALWAYS_ON_TYPES) {
    if (!mayShow(type)) continue;
    if (out.some((p) => p.type === type)) continue;
    const slot = fixedLayoutFor(role).find((s) => s.type === type)!;
    let id = `permanent-${type}`;
    for (let n = 2; ids.has(id); n += 1) id = `permanent-${type}-${n}`;
    ids.add(id);
    const bottom = out.reduce((m, p) => Math.max(m, p.row + p.h), 0);
    out.push({
      id, type, w: slot.w, h: slot.h, col: 0, row: bottom,
      ...(slot.config ? { config: { ...slot.config } } : {}),
    } as unknown as T);
  }
  return out;
}
